/**
 * @module Rendering
 */

import {
  addClass,
  classList,
  cloneNode,
  getId,
  hasClass,
  removeAttribute,
  setText,
} from "@acng/frontend-bounty";
import {append, removeChildNodes} from "@acng/frontend-bounty";
import {HTMLTemplateElement, isInstanceOf} from "@acng/frontend-bounty/types";
import {REGISTRY_ELEMENT} from "../model/resource.js";
import {isElementNode, isTextNode} from "@acng/frontend-bounty/dom/type.js";
import {ResourceUtility} from "../model/utility/resource.js";

const MODULE = "stargazer/render";

const rendering = Symbol();

/**
 * @param {string} name
 * @param {RenderAttrFn} fn
 * @returns {void}
 */
export function defineController(name, fn) {
  renderAttrs[name] = fn;
}

/**
 * @template {Scope} [S = Scope]
 * @template {Nodes} [N = Nodes]
 */
export class Rendering extends ResourceUtility {
  /**
   * @private
   * @type {RenderingContext<S, any, N>}
   */
  [rendering] = {
    [REGISTRY_ELEMENT]: this[REGISTRY_ELEMENT],
    [scope]: undefined,
  };

  /**
   * @returns {N | undefined}
   */
  get nodes() {
    return this[rendering][nodes];
  }

  clone() {
    return cloneNode(this[REGISTRY_ELEMENT].content);
  }

  /**
   * @template {HTMLElement} T
   * @param {T} target
   * @param {S} [params]
   * @param {(nodes: N, target: T) => void} [callback]
   * @returns {T}
   */
  toElement(target, params, callback) {
    const r = this[rendering];
    r[scope] = params ?? /** @type {S} */ ({});
    r[element] = target;
    r[nodes] = /** @type {N} */ ({});

    DEBUG: if (r[REGISTRY_ELEMENT].hasAttribute("debug")) {
      target.setAttribute("debug", "");
    }

    const fragment = render(r);

    callback?.(r[nodes], target);

    removeChildNodes(target);
    append(target, fragment);

    return target;
  }

  /**
   * @deprecated
   */
  get classList() {
    return this[REGISTRY_ELEMENT].classList;
  }

  /**
   * @param {S} [params]
   * @returns {string}
   */
  toText(params) {
    const r = this[rendering];
    r[scope] = params;
    return render(r).textContent ?? "";
  }
}

/**
 * @internal
 * @param {RenderingContext} ctx
 */
export const render = (ctx) => {
  const fragment = cloneNode(ctx[REGISTRY_ELEMENT].content);

  if (ctx[element]) {
    requireClassList(ctx[element], ctx[REGISTRY_ELEMENT]);
  }

  // TODO optimize? See RenderingContext Types
  if (ctx[scope] || ctx[nodes]) {
    renderChildNodes(fragment, ctx);
  }

  return fragment;
};

/**
 * @param {Element} element
 * @param {Element} source
 * @returns {void}
 */
const requireClassList = (element, source) => {
  DEBUG: if (element.hasAttribute("debug")) {
    for (const token of source.classList) {
      if (!hasClass(element, token)) {
        console.warn(`${MODULE} Add classes to satisfy css constraint.`, {
          element,
          classList: Array.from(source.classList),
        });
        break;
      }
    }
  }

  addClass(element, ...classList(source));
};

/**
 * @param {ParentNode} parentNode
 * @param {RenderingContext} ctx
 * @returns {void}
 */
const renderChildNodes = (parentNode, ctx) => {
  for (let i = parentNode.childNodes.length - 1; i >= 0; i--) {
    const childNode = parentNode.childNodes[i];
    if (isTextNode(childNode)) {
      renderTextNode(childNode, ctx);
    } else if (isInstanceOf(childNode, HTMLTemplateElement)) {
      renderChildNodes(childNode.content, ctx);
    } else if (isElementNode(childNode)) {
      renderElement(childNode, ctx);
    }
  }
};

/**
 * @todo
 * - Do not skip nodes when defined in scope.
 *
 * @param {Text} textNode
 * @param {RenderingContext} ctx
 * @returns {void}
 */
const renderTextNode = (textNode, ctx) => {
  const match = /^\${([^}]*)}$/.exec(textNode.nodeValue ?? "");

  if (match) {
    const key = match[1];

    if (ctx[scope]?.[key]) {
      setText(textNode, `${ctx[scope][key]}`);
    } else if (ctx[nodes]) {
      ASSERT: if (ctx[nodes][key] !== undefined)
        console.warn(`${MODULE} duplicate non-rendered Text key`, {key, textNode});

      ctx[nodes][key] = textNode;
    } else {
      ASSERT: console.warn(`${MODULE} orphaned text node`, {key, textNode});
    }
  }
};

/**
 * @param {Element} element
 * @param {RenderingContext} ctx
 * @returns {void}
 */
const renderElement = (element, ctx) => {
  const id = getId(element);

  if (id) {
    if (ctx[nodes]) {
      ASSERT: if (ctx[nodes][id] !== undefined) console.warn(`${MODULE} duplicate element id`, {element});

      ctx[nodes][id] = element;
    }

    removeAttribute(element, "id");
  }

  const {attributes} = element;

  for (let i = attributes.length - 1; i >= 0; i--) {
    renderAttrs[attributes[i].name]?.(element, attributes[i].value, ctx[scope], ctx);
  }

  //renderFunctions[element.localName]?.(element, null, ctx);
  renderChildNodes(element, ctx);
};

/**
 * @internal
 */
export const nodes = Symbol();

/**
 * @internal
 */
export const element = Symbol();

/**
 * @internal
 */
export const scope = Symbol();

/**
 * @type {Record<string, RenderAttrFn | undefined>}
 */
const renderAttrs = {};

/*
 * @callback RenderElementFn
 * @param {Element} element
 * @param {RenderingContext} rendering
 * @returns {void}
 */

/**
 * @callback RenderAttrFn
 * @param {Element} element
 * @param {string} value
 * @param {Scope | undefined} scope
 * @param {RenderingContext} rendering
 * @returns {void}
 */

/**
 * @typedef {Record<string, any>} Scope
 */

/**
 * @typedef {Record<string, Element | Text | undefined>} Nodes
 */

/**
 * TODO put transcluded stuff in here
 * TODO this should be the same class TemplateUtility => class Render
 * TODO intern können die Objekte mit Object.create erzeugt werden.
 *
 * @template {Scope} [S = Scope]
 * @template {Element} [E = Element]
 * @template {Nodes} [N = Nodes]
 * @typedef {{
 *   [REGISTRY_ELEMENT]: HTMLTemplateElement;
 *   [scope]: S | undefined;
 *   [element]?: E;
 *   [nodes]?: N;
 * }} RenderingContext
 */
